/**
 * Hook: Check if G14 dynamic price data exists for a year
 *
 * Calls hasDynamicPriceData GraphQL query (public + authenticated).
 * Returns availability status for UI conditional rendering.
 */

import { useState, useEffect, useCallback } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';

export interface UseHasDynamicPriceDataReturn {
  hasData: boolean;
  monthCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useHasDynamicPriceData(year: number): UseHasDynamicPriceDataReturn {
  const [hasData, setHasData] = useState(false);
  const [monthCount, setMonthCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const refetch = useCallback(() => {
    setRefetchCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!year || year < 2020 || year > 2040) {
        setHasData(false);
        setMonthCount(0);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const client = getAmplifyClient();
        const result = await client.queries.hasDynamicPriceData({
          input: JSON.stringify({ year }),
        });

        if (cancelled) return;

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const data = typeof result.data === 'string'
          ? JSON.parse(result.data)
          : result.data;

        setHasData(data?.hasData ?? false);
        setMonthCount(data?.monthCount ?? 0);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to check G14 data');
        setHasData(false);
        setMonthCount(0);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchData();
    return () => { cancelled = true; };
  }, [year, refetchCount]);

  return { hasData, monthCount, loading, error, refetch };
}
